import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@drpcrd/utils/shared";

const typingIndicatorVariants = cva("flex items-center justify-start", {
  variants: {
    size: {
      xs: "gap-0.5",
      sm: "gap-1",
      md: "gap-1.5",
      lg: "gap-2",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const dotVariants = cva("rounded-full animate-pulse", {
  variants: {
    size: {
      xs: "h-1 w-1",
      sm: "h-2 w-2",
      md: "h-3 w-3",
      lg: "h-4 w-4",
    },
    color: {
      primary: "bg-primary-100",
      secondary: "bg-neutral-300",
    },
  },
  defaultVariants: {
    size: "md",
    color: "primary",
  },
});

export type TypingIndicatorProps = VariantProps<
  typeof typingIndicatorVariants
> &
  Pick<VariantProps<typeof dotVariants>, "color"> & {
    /**
     * Number of dots to display in the indicator
     */
    dotCount?: number;
    /**
     * Animation duration in seconds
     */
    animationDuration?: string;
    /**
     * Custom class name for the container
     */
    className?: string;
    /**
     * Custom class name for individual dots
     */
    dotClassName?: string;
    /**
     * Whether to stagger the animation of dots
     */
    staggered?: boolean;
  };

/**
 * A customizable typing indicator component that shows animated dots
 * to indicate when something is loading or typing
 */
export const TypingIndicator = ({
  size,
  dotCount = 1,
  color,
  animationDuration = "2s",
  className,
  dotClassName,
  staggered = false,
  ...props
}: TypingIndicatorProps) => {
  const dots = Array.from({ length: dotCount }, (_, index) => (
    <div
      key={index}
      className={cn(dotVariants({ size, color }), dotClassName)}
      style={{
        animationDuration,
        ...(staggered && {
          animationDelay: `${index * 0.2}s`,
        }),
      }}
    />
  ));

  return (
    <div
      className={cn(typingIndicatorVariants({ size }), className)}
      {...props}
    >
      {dots}
    </div>
  );
};
